import React, { useState, useRef } from "react";
import "../styles/pinEntryModal.scss";

/**
 * PinEntryModal — 4-digit PIN entry dark modal
 * Figma: 1.3 Change profile (Desktop) (24654:5042) — 570×429
 *
 * Dark card (#262626) with header/body/footer sections.
 *   - Header: "Enter pin to access to this profile" + subtitle + close (X)
 *   - Body: 4 PIN input boxes (64×64, dark styled)
 *   - Footer: "Forgot pin" outline button
 *
 * @param {boolean} isOpen — show/hide modal
 * @param {function} onClose — close handler
 * @param {function} onSubmit — called with 4-digit PIN string
 */

const PinEntryModal = ({ isOpen, onClose, onSubmit }) => {
  const [pin, setPin] = useState(["", "", "", ""]);
  const inputRefs = useRef([]);

  if (!isOpen) return null;

  const handleChange = (index, value) => {
    if (value.length > 1) return;
    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }

    // Submit when all 4 digits entered
    if (index === 3 && value) {
      const fullPin = newPin.join("");
      if (fullPin.length === 4) {
        onSubmit?.(fullPin);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !pin[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  return (
    <div className="pin-overlay" onClick={onClose}>
      <div className="pin-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="pin-modal__header">
          <div className="pin-modal__header-text">
            <h2 className="pin-modal__title">Enter pin to access to this profile</h2>
            <p className="pin-modal__subtitle">Enter your 4 PIN password to continue</p>
          </div>
          <button className="pin-modal__close" onClick={onClose} aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="#a3a3a3" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Body — PIN inputs */}
        <div className="pin-modal__body">
          <div className="pin-modal__inputs">
            {[0, 1, 2, 3].map((i) => (
              <input
                key={i}
                ref={(el) => (inputRefs.current[i] = el)}
                type="password"
                className="pin-modal__input"
                maxLength={1}
                value={pin[i]}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                autoFocus={i === 0}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="pin-modal__footer">
          <button className="pin-modal__forgot">Forgot pin</button>
        </div>
      </div>
    </div>
  );
};

export default PinEntryModal;
